import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import './Layout.css';

const Layout = ({ children, nav, activeNav, onNavChange, onLogout, title, headerLabel }) => {
  const { account } = useWallet();
  // Default open on desktop, closed on mobile (there it overlays the content).
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 768 : true));
  const [theme, setTheme] = useState(() => localStorage.getItem('robo-theme') || 'dark');

  // On mobile, selecting a nav item should close the overlay sidebar.
  const handleNav = (key) => {
    onNavChange(key);
    if (typeof window !== 'undefined' && window.innerWidth <= 768) setSidebarOpen(false);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('robo-theme', theme);
  }, [theme]);

  // ─── Grouped nav support (backward compatible with a flat nav array) ───
  const isGrouped = Array.isArray(nav) && nav.some((n) => n && n.items);
  const [groupOpen, setGroupOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem('robo-nav-groups')) || {}; } catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem('robo-nav-groups', JSON.stringify(groupOpen));
  }, [groupOpen]);
  useEffect(() => {
    if (!isGrouped) return;
    const g = nav.find((e) => e.items && e.items.some((i) => i.key === activeNav));
    if (g) setGroupOpen((prev) => ({ ...prev, [g.group]: true }));
  }, [activeNav, isGrouped, nav]);
  const toggleGroup = (g) => setGroupOpen((prev) => ({ ...prev, [g]: !(prev[g] ?? true) }));

  const NavItem = (item) => (
    <button
      key={item.key}
      className={`sidebar-nav-item ${activeNav === item.key ? 'active' : ''}`}
      onClick={() => handleNav(item.key)}
      title={item.badge > 0 ? `${item.label} — ${item.badge} pending` : item.label}
    >
      <span className="sidebar-nav-icon">
        {item.icon}
        {item.badge > 0 && !sidebarOpen && <span className="sidebar-nav-dot" />}
      </span>
      {sidebarOpen && <span className="sidebar-nav-label">{item.label}</span>}
      {sidebarOpen && item.badge > 0 && <span className="sidebar-nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
    </button>
  );

  const truncAddr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '';

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <span className="sidebar-logo-icon">◈</span>
            {sidebarOpen && (
              <>
                <span className="sidebar-logo-text">ROBO</span>
                <span className="sidebar-logo-badge">{title || 'TOKEN'}</span>
              </>
            )}
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? '◁' : '▷'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {nav.map((entry) => {
            // Flat item (no group) — original behaviour
            if (!entry.items) return NavItem(entry);

            // Grouped item — collapsible header + children
            const expanded = !sidebarOpen || (groupOpen[entry.group] ?? true);
            const groupBadge = (entry.items || []).reduce((s, i) => s + (i.badge || 0), 0);
            return (
              <div className="sidebar-group" key={entry.group}>
                {sidebarOpen && (
                  <button className="sidebar-group-header" onClick={() => toggleGroup(entry.group)}>
                    <span className="sidebar-group-title">{entry.label || entry.group}</span>
                    {groupBadge > 0 && !expanded && <span className="sidebar-nav-badge">{groupBadge > 99 ? '99+' : groupBadge}</span>}
                    <span className={`sidebar-group-caret ${expanded ? 'open' : ''}`}>▸</span>
                  </button>
                )}
                {expanded && entry.items.map((item) => NavItem(item))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-bottom">
          <button className="sidebar-btn" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
            <span className="sidebar-nav-icon">{theme === 'dark' ? '☀' : '🌙'}</span>
            {sidebarOpen && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
          {onLogout && (
            <button className="sidebar-btn logout" onClick={onLogout}>
              <span className="sidebar-nav-icon">⏻</span>
              {sidebarOpen && <span>Logout</span>}
            </button>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className="layout-main">
        {/* Top header */}
        <header className="layout-header">
          <button className="sidebar-mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <div className="layout-header-right">
            {headerLabel ? (
              <div className="layout-wallet">
                <span className="wallet-dot" />
                <span>{headerLabel}</span>
              </div>
            ) : account ? (
              <div className="layout-wallet">
                <span className="wallet-dot" />
                <span className="mono">{truncAddr(account)}</span>
              </div>
            ) : null}
          </div>
        </header>

        {/* Page content */}
        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;